// The editor side of the Wizard. The Wizard itself runs on the server (SPEC §7): this module only
// asks for suggestions, previews them (ghost notes, audible) and merges an accepted one.
import { create } from "zustand";
import { sortEvents, type LoopData, type NoteEvent, type SoundMix } from "@eb/shared";
import { api, ApiError } from "../api";
import { edit, editSong, mergeView, patternView, useEditor } from "./store";

export type WizardKind = "groove" | "fill" | "chords" | "bass" | "melody" | "arp" | "harmony" | "complete";

export interface Suggestion {
  kind: WizardKind;
  events: NoteEvent[];
  replace: { slots: number[]; fromTick: number; toTick: number };
  description: string;
  chords: string[];
  seed: number;
}

export const KIND_TARGET: Record<WizardKind, number> = {
  groove: 0,
  fill: 0,
  chords: 17,
  bass: 16,
  melody: 18,
  arp: 17,
  harmony: 18,
  complete: 16,
};
export const KIND_LABEL: Record<WizardKind, string> = {
  groove: "Drum groove",
  fill: "Fill",
  chords: "Chords",
  bass: "Bass line",
  melody: "Melody",
  arp: "Arpeggio",
  harmony: "Harmony line",
  complete: "Complete this pattern",
};

interface WizardState {
  open: boolean;
  kind: WizardKind;
  target: number;
  genre: string;
  mood: string;
  complexity: number;
  density: number;
  suggestion: Suggestion | null;
  busy: boolean;
  error: string;
  /** The mark this loop would get right now (server-computed), or null. */
  markPreview: number | null;
  set: (p: Partial<Omit<WizardState, "set">>) => void;
}

export const useWizard = create<WizardState>((set) => ({
  open: false,
  kind: "melody",
  target: 18,
  genre: "",
  mood: "",
  complexity: 0.4,
  density: 0.5,
  suggestion: null,
  busy: false,
  error: "",
  markPreview: null,
  set: (p) => set(p),
}));

/** Replace the suggestion's region in its slots and add its events (pure). */
export function mergeSuggestion(loop: LoopData, s: Pick<Suggestion, "events" | "replace">): LoopData {
  const { slots, fromTick, toTick } = s.replace;
  const kept = loop.events.filter((e) => !(slots.includes(e.pad) && e.tick >= fromTick && e.tick < toTick));
  const seen = new Set<string>();
  const events: NoteEvent[] = [];
  for (const e of [...kept, ...s.events]) {
    const k = `${e.pad}:${e.pad >= 16 ? e.note : 60}:${e.tick}`;
    if (seen.has(k)) continue;
    seen.add(k);
    events.push(e);
  }
  return { ...loop, events: sortEvents(events) };
}

/** The loop the engine plays while a suggestion is being previewed. */
/** The song with the pending suggestion (if any) shown in pattern `pattern`. */
export function previewLoop(loop: LoopData, pattern = 0): LoopData {
  const s = useWizard.getState().suggestion;
  return s ? mergeView(loop, pattern, mergeSuggestion(patternView(loop, pattern), s)) : loop;
}

export async function conjure(newIdea = true): Promise<void> {
  const w = useWizard.getState();
  const ed = useEditor.getState();
  if (!ed.server) return;
  w.set({ busy: true, error: "" });
  try {
    const seed = newIdea || !w.suggestion ? Math.floor(Math.random() * 0xffffffff) : w.suggestion.seed;
    const body: Record<string, unknown> = {
      kind: w.kind,
      target: w.target,
      complexity: w.complexity,
      density: w.density,
      seed,
      // The Wizard writes into the pattern being edited.
      loop: patternView(ed.hist.present, ed.pattern),
    };
    if (w.genre) body.genre = w.genre;
    if (w.mood) body.mood = w.mood;
    const r = await api<{ suggestion: Suggestion }>("POST", `/api/drafts/${ed.server.id}/wizard`, body);
    w.set({ suggestion: r.suggestion, busy: false });
  } catch (e) {
    w.set({ busy: false, error: e instanceof ApiError ? e.message : String(e) });
  }
}

export function acceptSuggestion(): void {
  const s = useWizard.getState().suggestion;
  if (!s) return;
  edit((l) => mergeSuggestion(l, s));
  useWizard.getState().set({ suggestion: null });
}

export function discardSuggestion(): void {
  useWizard.getState().set({ suggestion: null });
}

let markTimer: ReturnType<typeof setTimeout> | undefined;

/** Refresh the mark preview (debounced) from the server. */
export function refreshMark(): void {
  clearTimeout(markTimer);
  markTimer = setTimeout(async () => {
    const ed = useEditor.getState();
    if (!ed.server || ed.server.status === "approved") return;
    try {
      const r = await api<{ wizardShare: number }>("POST", `/api/drafts/${ed.server.id}/mark`, {
        loop: ed.hist.present,
      });
      useWizard.getState().set({ markPreview: r.wizardShare });
    } catch {
      /* preview only */
    }
  }, 600);
}

// A suggestion belongs to the pattern it was made for: switching patterns drops it.
useEditor.subscribe((s, p) => {
  if (s.pattern !== p.pattern && useWizard.getState().suggestion) discardSuggestion();
});

/** The Wizard's mix-down: the whole song balanced on the server (one undo step). */
export async function mixDown(): Promise<string[]> {
  const ed = useEditor.getState();
  if (!ed.server) return [];
  const r = await api<{ mixdown: { mix: SoundMix[]; changes: string[] } }>(
    "POST",
    `/api/drafts/${ed.server.id}/mixdown`,
    { loop: ed.hist.present },
  );
  editSong((l) => ({ ...l, mix: r.mixdown.mix }));
  return r.mixdown.changes;
}
